import { useState, useEffect, ReactNode, useCallback } from 'react';
import { api } from '../services/api';
import { AuthContext, type RegisterData, type User } from './useAuth';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Check if user has a valid session by calling the session endpoint
   * This validates the HttpOnly cookie on the server
   */
  const checkSession = useCallback(async () => {
    try {
      const response = await api.get('/auth/session');
      setUser(response.data.data.user);
    } catch {
      // Session invalid or expired - user is not authenticated
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check session on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  /**
   * Login - cookies are set automatically by the server
   */
  const login = async (email: string, password: string): Promise<User> => {
    const response = await api.post('/auth/login', { email, password });
    const { user: newUser } = response.data.data;
    setUser(newUser);
    return newUser;
  };

  /**
   * Register - cookies are set automatically by the server
   */
  const register = async (data: RegisterData): Promise<User> => {
    const response = await api.post('/auth/register', data);
    const { user: newUser } = response.data.data;
    setUser(newUser);
    return newUser;
  };

  /**
   * Logout - clears cookies on the server
   */
  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Even if logout fails on server, clear local state
    }
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        checkSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
